import { View, Text, styled, ScrollView } from 'tamagui'
import { Bell, Volume2, MessageCircle } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'

const Container = styled(View, { flex: 1, flexDirection: 'column' })
const Header = styled(View, { paddingHorizontal: 32, paddingVertical: 20, borderBottomWidth: 1, borderBottomColor: '$borderDefault' })
const Title = styled(Text, { fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground' })
const Body = styled(View, { padding: 32, gap: 24, maxWidth: 600 })
const Section = styled(View, { gap: 12 })
const SectionTitle = styled(Text, { fontFamily: '$fontBody', fontWeight: '600', fontSize: 16, color: '$surfaceForeground' })

const SettingRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  paddingVertical: 10, paddingHorizontal: 12, borderRadius: '$borderRadiusLg',
  borderWidth: 1, borderColor: '$borderDefault', gap: 12,
})

const Toggle = styled(View, {
  width: 40, height: 22, borderRadius: '$borderRadiusXl', cursor: 'pointer', justifyContent: 'center',
  variants: { active: { true: { backgroundColor: '$accent' }, false: { backgroundColor: '$sidebarActive' } } } as const,
})

const ToggleKnob = styled(View, {
  width: 18, height: 18, borderRadius: '$borderRadiusLg', backgroundColor: '$surfaceForeground',
  // @ts-expect-error -- web transition
  style: { transition: 'margin-left 150ms ease' },
})

const RadioGroup = styled(View, { gap: 4, paddingLeft: 4 })

const RadioItem = styled(View, {
  flexDirection: 'row', alignItems: 'center', height: 36, gap: 10,
  paddingHorizontal: 12, borderRadius: '$borderRadiusMd', cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const RadioDot = styled(View, {
  width: 16, height: 16, borderRadius: '$borderRadiusLg', borderWidth: 2, borderColor: '$borderDefault',
  alignItems: 'center', justifyContent: 'center',
  variants: { active: { true: { borderColor: '$accent' } } } as const,
})

const RadioFill = styled(View, { width: 8, height: 8, borderRadius: '$borderRadiusSm', backgroundColor: '$accent' })

export function NotificationSettings() {
  const {
    desktopNotifications, soundEnabled, dmNotificationLevel,
    setDesktopNotifications, setSoundEnabled, setDmNotificationLevel,
  } = useAppStore()

  return (
    <Container>
      <Header><Title>Notifications</Title></Header>
      <ScrollView flex={1}>
        <Body>
          <Section>
            <SectionTitle>General</SectionTitle>
            <SettingRow>
              <View flexDirection="row" alignItems="center" gap={8} flex={1}>
                <Bell size={16} color="$sidebarMuted" />
                <View flex={1} gap={2}>
                  <Text fontFamily="$fontBody" fontSize={14} fontWeight="500" color="$surfaceForeground">Desktop Notifications</Text>
                  <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">Show system notifications for new messages</Text>
                </View>
              </View>
              <Toggle active={desktopNotifications} onPress={() => setDesktopNotifications(!desktopNotifications)}>
                <ToggleKnob marginLeft={desktopNotifications ? 20 : 2} />
              </Toggle>
            </SettingRow>

            <SettingRow>
              <View flexDirection="row" alignItems="center" gap={8} flex={1}>
                <Volume2 size={16} color="$sidebarMuted" />
                <View flex={1} gap={2}>
                  <Text fontFamily="$fontBody" fontSize={14} fontWeight="500" color="$surfaceForeground">Notification Sounds</Text>
                  <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">Play sounds for notifications</Text>
                </View>
              </View>
              <Toggle active={soundEnabled} onPress={() => setSoundEnabled(!soundEnabled)}>
                <ToggleKnob marginLeft={soundEnabled ? 20 : 2} />
              </Toggle>
            </SettingRow>
          </Section>

          <Section>
            <SectionTitle>Direct Messages</SectionTitle>
            <View flexDirection="row" alignItems="center" gap={8}>
              <MessageCircle size={16} color="$sidebarMuted" />
              <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
                Choose when to receive DM notifications.
              </Text>
            </View>
            <RadioGroup>
              {([
                { value: 'all' as const, label: 'All Messages', desc: 'Notify for every DM' },
                { value: 'mentions' as const, label: 'Mentions Only', desc: 'Only when someone mentions you' },
                { value: 'nothing' as const, label: 'Nothing', desc: 'Mute all DM notifications' },
              ]).map((opt) => (
                <RadioItem key={opt.value} onPress={() => setDmNotificationLevel(opt.value)}>
                  <RadioDot active={dmNotificationLevel === opt.value}>
                    {dmNotificationLevel === opt.value && <RadioFill />}
                  </RadioDot>
                  <View flex={1}>
                    <Text fontFamily="$fontBody" fontSize={14} color="$surfaceForeground">{opt.label}</Text>
                    <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">{opt.desc}</Text>
                  </View>
                </RadioItem>
              ))}
            </RadioGroup>
          </Section>
        </Body>
      </ScrollView>
    </Container>
  )
}
